import { CompensationHistoryInterface } from '../../models/history';
import { GridRowClassNameParams } from '@mui/x-data-grid'
import { NavigateFunction } from 'react-router-dom';
import { ResendCompensationPayload, UpdateEmailPayload } from '../../models/common';
import { useHistoryProvider } from '../../providers/HistoryProvider';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import enTranslations from '../../assets/i18n/en.json';
import frTranslations from '../../assets/i18n/fr.json';
import { HISTORY_VIEW } from '../../constants/common';
import { useMasterData } from '../../providers/MasterDataProvider';
import { Field } from '../../models/home';

interface Props {
    rows: CompensationHistoryInterface[];
    navigate: NavigateFunction;
    searchFields?: Field[];
    historyView: string;
}

export const useCompensationHistoryGridResults = ({rows, navigate, searchFields, historyView}: Props) => {
    const historyContext = useHistoryProvider();
    const { t, i18n } = useTranslation();
    const { masterData } = useMasterData();
    
    const defaultResults = rows.findIndex(r => r?.id?.startsWith('default')) > -1;
    const [openUpdateEmailDialog, setOpenUpdateEmailDialog] = useState(false);
    const [openSentConfirmation, setOpenSentConfirmation] = useState<boolean>(false);
    const [updateEmailPayload, setUpdateEmailPayload] = useState<UpdateEmailPayload>({
        requestId: 'empty',
        voucherId: '',
        passengerEmailAddress: '',
        voucherType: 'WIFI',
    });

    useEffect(() => {
        if (historyContext.emailSent) {
            setOpenSentConfirmation(true);
        }
    }, [historyContext.emailSent]);

    
    const handleCloseEmailUpdatedConfirmation = () => {
        historyContext.setEmailUpdated(false);
        historyContext.setEmailSent(0);
        setOpenSentConfirmation(false);
    };

    const editErroredVoucher = (row) => {
        let cleanSubType = row?.subType;
        if (typeof cleanSubType === 'string' && cleanSubType.split('_').length > 2) {
            const parts = cleanSubType.split('_');
            cleanSubType = parts.slice(parts.length - 2).join('_');
        }
        
        // Extract percentage from subType for NTP compensation type
        let compensationType = row?.type;
        if (row?.type === 'NTP' && cleanSubType) {
            // Extract the percentage number from subType (e.g., "percent_15" -> "15")
            const percentageMatch = cleanSubType.match(/percent_(\d+)/);
            if (percentageMatch) {
                const percentage = percentageMatch[1];
                compensationType = t(`compensationTypes.${percentage}`);
            } else {
                // Fallback to generic promotional code if percentage can't be extracted
                compensationType = t('compensationTypes.promotionalCode');
            }
        } else if (row?.type === 'WIFI') {
            compensationType = 'Wi-Fi';
        }

        // Helper functions to get display text from master data
        const getReasonText = (reasonId) => {
            if (!reasonId) return '';
            const reason = masterData.reasons.find(r => r.reasonId === reasonId);
            if (!reason) return row?.reason || '';
            return i18n.language === 'fr' 
                ? (reason.reasonTitleFr || reason.reasonTitleEn || '') 
                : (reason.reasonTitleEn || reason.reasonTitleFr || '');
        };

        const getCategoryText = (categoryId) => {
            if (!categoryId) return '';
            const category = masterData.categories.find(c => c.categoryId === categoryId);
            if (!category) return '';
            return i18n.language === 'fr' 
                ? (category.categoryTitleFr || category.categoryTitleEn || '') 
                : (category.categoryTitleEn || category.categoryTitleFr || '');
        };

        const getCabinText = (cabinId, categoryId) => {
            if (!cabinId) return '';
            // First try to find in top-level cabins
            const cabin = masterData.cabins.find(c => c.cabinId === cabinId);
            if (cabin) {
                return i18n.language === 'fr' 
                    ? (cabin.cabinTitleFr || cabin.cabinTitleEn || '') 
                    : (cabin.cabinTitleEn || cabin.cabinTitleFr || '');
            }
            // Try to find in category's cabins
            if (categoryId) {
                const category = masterData.categories.find(c => c.categoryId === categoryId);
                const nestedCabin = category?.cabins?.find(c => c.cabinId === cabinId);
                if (nestedCabin) {
                    // First try to find the cabin in master data by ID
                    const masterCabin = masterData.cabins.find(c => c.cabinId === nestedCabin.cabinId);
                    if (masterCabin) {
                        return i18n.language === 'fr' 
                            ? (masterCabin.cabinTitleFr || masterCabin.cabinTitleEn || '') 
                            : (masterCabin.cabinTitleEn || masterCabin.cabinTitleFr || '');
                    }
                    // Fall back to shorthand text if no master cabin found
                    return nestedCabin.shorthandTxt || '';
                }
            }
            return '';
        };
        
        // Get the display text for cabin
        const cabinDisplayText = getCabinText(row?.cabinId, row?.categoryId);
        
        const state = {
            bookingReference: row?.bookingReference,
            comments: row?.comments || '',
            customer: row?.customer || '',
            date: row?.date || '',
            email: row?.email || '',
            flight: row?.flight || '',
            id: row?.id || '',
            qty: row?.qty ?? 1,
            reason: getReasonText(row?.reasonId) || getTranslatedReason(row?.reason) || '',
            reasonId: row?.reasonId || '',
            category: getCategoryText(row?.categoryId) || '',
            categoryId: row?.categoryId || '',
            cabin: cabinDisplayText,
            cabinId: row?.cabinId || '',
            subType: cleanSubType || '',
            type: compensationType || '',
            requestId: row?.requestId || ''
        };
        sessionStorage.setItem('HistoryEditFields', JSON.stringify(state));
        navigate(`/${i18n.language}`);
    };

    const getRowClassName = (params: GridRowClassNameParams<any>) => {
        if (params.row.error) return 'c-cell-error';
        const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
        if (status === 'PROCESSING') return 'c-cell-processing';
        if (status === 'QUEUED') return 'c-cell-queued';
        if (status === 'VALIDATING') return 'c-cell-validating';
        return '';
    };

    const getServerSidePaginationCount = () => {
        let count = historyContext.historyNonIfsPageState.size*((historyContext.historyNonIfsPageState.number ?? 0)+(defaultResults ? 1 : 2));
        if (rows.length < historyContext.historyNonIfsPageState.size) {
            count = ((historyContext.historyNonIfsPageState.number ?? 0) + 1) * historyContext.historyNonIfsPageState.size - (historyContext.historyNonIfsPageState.size - rows.length)
        }
        return count;
    };

    const getTranslatedReason = (reason: string | undefined) => {
        if (!reason || !enTranslations?.groupScenarios || !frTranslations?.groupScenarios) {
            return;
        }

        const englishScenarios = enTranslations.groupScenarios;
        // First try exact match
        const exactEnglishKey = Object.keys(englishScenarios).find(key => englishScenarios[key] === reason);
        if (exactEnglishKey) return t(`groupScenarios.${exactEnglishKey}`);

        // Fallback to startsWith for backward compatibility
        const englishKey = Object.keys(englishScenarios).find(key => englishScenarios[key].startsWith(reason));
        if (englishKey) return t(`groupScenarios.${englishKey}`);

        const frenchScenarios = frTranslations.groupScenarios;
        // First try exact match
        const exactFrenchKey = Object.keys(frenchScenarios).find(key => frenchScenarios[key] === reason);
        if (exactFrenchKey) return t(`groupScenarios.${exactFrenchKey}`);

        // Fallback to startsWith for backward compatibility
        const frenchKey = Object.keys(frenchScenarios).find(key => frenchScenarios[key].startsWith(reason));
        if (frenchKey) return t(`groupScenarios.${frenchKey}`);
        return;
    };

    // Map IDs to display text using master data. If not found, return undefined.
    const getReasonTitleById = (reasonId?: string) => {
        if (!reasonId) return undefined;
        const match = masterData.reasons.find(r => r.reasonId === reasonId);
        if (!match) return undefined;
        if (i18n.language === 'fr') {
            const fr = (match.reasonTitleFr || '').toString().trim();
            if (fr) return fr;
            const en = (match.reasonTitleEn || '').toString().trim();
            return en || undefined;
        }
        const en = (match.reasonTitleEn || '').toString().trim();
        return en || (match.reasonTitleFr || '').toString().trim() || undefined;
    };

    const getCategoryTitleById = (categoryId?: string) => {
        if (!categoryId) return undefined;
        const match = masterData.categories.find(c => c.categoryId === categoryId);
        if (!match) return undefined;
        if (i18n.language === 'fr') {
            const fr = (match.categoryTitleFr || '').toString().trim();
            if (fr) return fr;
            const en = (match.categoryTitleEn || '').toString().trim();
            return en || undefined;
        }
        const en = (match.categoryTitleEn || '').toString().trim();
        return en || (match.categoryTitleFr || '').toString().trim() || undefined;
    };

    const getCabinTitleById = (cabinId?: string, categoryId?: string) => {
        if (!cabinId) return undefined;
        // Prefer cabin title from top-level cabins list
        const cabin = masterData.cabins.find(c => c.cabinId === cabinId);
        if (cabin) {
            if (i18n.language === 'fr') {
                const fr = (cabin.cabinTitleFr || '').toString().trim();
                if (fr) return fr;
                const en = (cabin.cabinTitleEn || '').toString().trim();
                return en || undefined;
            }
            const en = (cabin.cabinTitleEn || '').toString().trim();
            return en || (cabin.cabinTitleFr || '').toString().trim() || undefined;
        }
        // Fallback: some cabins may be nested under category mapping with only shorthand
        if (categoryId) {
            const cat = masterData.categories.find(c => c.categoryId === categoryId);
            const nested = cat?.cabins?.find(x => x.cabinId === cabinId);
            if (nested && typeof nested.shorthandTxt === 'string' && nested.shorthandTxt) return nested.shorthandTxt;
        }
        return undefined;
    };
    
    // const handleOnClickShowDetails = (row: CompensationHistoryInterface, view: string) => {
    //     const element = view === VIEW_TYPE.web 
    //         ? document.querySelector(`.c-web-${row.id}`) 
    //         : document.querySelector(`.c-ipad-${row.id}`);
    //     const detailIcons = document.querySelectorAll(`.icon-expand-${row.id}`);
    //     if (element) {
    //         element.classList.toggle('c-details-show');
    //     }
    //     if (detailIcons && detailIcons.length) {
    //         detailIcons.forEach((icon) => {
    //             icon.classList.toggle('icon-expand-show');
    //         });
    //     }
    // };

    const sendCompensationCodes = (row) => {
        const resendPayload: ResendCompensationPayload = {
            pnrLocator: row.bookingReference,
            voucherIds: Array.isArray(row.voucherIds) && row.voucherIds.length > 0 ? row.voucherIds : (row.id ? [row.id] : []),
            voucherStatus: row.status === 'COMPLETED' ? 'REL' : row.status,
            voucherType: row.type
        };
        historyContext.resendCompensation(resendPayload);
    };

    const updateEmailAddress = (row) => {
        setUpdateEmailPayload({
            requestId: row.requestId || '',
            voucherId: row.id || '',
            passengerEmailAddress: row.email,
            voucherType: row.type || 'WIFI',
        });
        setOpenUpdateEmailDialog(true);
    };

    
    const saveUpdatedEmailAddress = async (updateEmailPayload) => {
        const shouldSearchNonIFS = historyView === HISTORY_VIEW.nonIfs;
        await historyContext.updateEmailAddress(updateEmailPayload, searchFields || [], shouldSearchNonIFS);
    };

    // Utility to display subType as '15 %' if it matches 'percent_15', etc.
    const getDisplaySubType = (subType: string) => {
        if (typeof subType === 'string' && subType.startsWith('percent_')) {
            const value = subType.split('_')[1];
            return value + ' %';
        }
        return subType;
    };

    return {
        historyContext,
        defaultResults,
        i18n,
        openUpdateEmailDialog,
        updateEmailPayload,
        editErroredVoucher,
        getRowClassName,
        getServerSidePaginationCount,
        getTranslatedReason,
        getReasonTitleById,
        getCategoryTitleById,
        getCabinTitleById,
        sendCompensationCodes,
        setOpenUpdateEmailDialog,
        setUpdateEmailPayload,
        t,
        updateEmailAddress,
        openSentConfirmation,
        handleCloseEmailUpdatedConfirmation,
        saveUpdatedEmailAddress,
        getDisplaySubType
    };
};

